import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@repo/videa-components/src/index';
import { useAtom } from 'jotai';
import { AlertCircle, AlertTriangle, ExternalLink, Mic } from 'lucide-react';

import { hasMicrophonePermissionAtom } from '../../atoms';
import { AudioFileUpload } from '../microphone/AudioFileUpload';

import { useAudioDeviceManager } from './useAudioDeviceManager';

interface MicrophonePermissionFlowProps {
  onPermissionGranted: () => void;
  onAudioFileSelected?: (file: File, blobUrl: string) => void;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
}

const getErrorMessage = (err: unknown): string => {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Microphone access was denied. Please allow access to continue.';
      case 'NotFoundError':
        return 'No microphone found. Please connect a microphone and try again.';
      case 'NotReadableError':
        return 'Could not access the microphone. It may be in use by another application.';
      case 'OverconstrainedError':
        return 'Your microphone does not support the required settings.';
      case 'AbortError':
        return 'Microphone access was interrupted. Please try again.';
      case 'SecurityError':
        return 'Microphone access is blocked by security settings.';
      default:
        return `Microphone error: ${err.message}`;
    }
  }
  return 'An unexpected error occurred while accessing the microphone.';
};

const MicrophonePermissionFlow: FC<MicrophonePermissionFlowProps> = ({
  onPermissionGranted,
  onAudioFileSelected,
  isAudioUploadEnabled = false,
  showDevTools = false,
}) => {
  const [permissionState, setPermissionState] = useState<
    'idle' | 'requesting' | 'granted' | 'denied' | 'error'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [, setHasMicrophonePermission] = useAtom(hasMicrophonePermissionAtom);

  const { refreshDevices } = useAudioDeviceManager();

  // Check if permission is already granted on mount
  useEffect(() => {
    const checkExistingPermission = async () => {
      try {
        // Use the Permissions API to check current status without prompting
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        if (result.state === 'granted') {
          // Permission already granted, try to get a stream to confirm it works
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          // Stop the stream immediately - we just needed to verify access
          stream.getTracks().forEach((track) => track.stop());
          setPermissionState('granted');
          setHasMicrophonePermission(true);
          await refreshDevices();
          onPermissionGranted();
        }
      } catch {
        // Permissions API may not be available or query failed - that's okay
        // We'll fall back to the normal flow
      }
    };
    checkExistingPermission();
  }, [onPermissionGranted, refreshDevices, setHasMicrophonePermission]);

  const handleRequestPermission = async () => {
    try {
      setPermissionState('requesting');
      setError(null);

      // Request permission with generic audio constraint
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach((track) => track.stop());

      setPermissionState('granted');
      setRetryCount(0); // Reset retry count on success
      setHasMicrophonePermission(true);
      // Refresh devices after permission is granted
      await refreshDevices();
      onPermissionGranted();
    } catch (err) {
      console.error('Microphone access error:', err);
      const isDenied =
        err instanceof DOMException && err.name === 'NotAllowedError';
      setPermissionState(isDenied ? 'denied' : 'error');
      setRetryCount((prev) => prev + 1);
      setError(getErrorMessage(err));
    }
  };

  if (permissionState === 'granted') {
    return null; // Hide this component once permission is granted
  }

  return (
    <div className="vai-flex-col vai-p-4 vai-w-full">
      <div className="vai-flex vai-p-4 vai-w-full vai-bg-blue-50 vai-border vai-border-blue-200 vai-rounded-lg">
        <div className="vai-flex vai-items-start vai-gap-3 vai-w-full ">
          <div className="vai-flex-1">
            <h3 className="vai-text-sm vai-font-bold vai-mb-2">
              Microphone Access Required
            </h3>
            <p className="vai-text-sm vai-mb-4">
              To record audio notes, we need access to your microphone.
            </p>
            {permissionState === 'denied' && (
              <div className="vai-p-3 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded vai-mb-4">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <AlertTriangle className="vai-w-4 vai-h-4 vai-text-red-500" />
                  <p className="vai-text-sm vai-text-red-700">{error}</p>
                </div>
              </div>
            )}
            {permissionState === 'error' && (
              <div className="vai-p-3 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded vai-mb-4">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <AlertCircle className="vai-w-4 vai-h-4 vai-text-yellow-600" />
                  <p className="vai-text-sm vai-text-yellow-700">{error}</p>
                </div>
              </div>
            )}

            <div className="vai-flex vai-justify-end">
              <Button
                onClick={handleRequestPermission}
                disabled={permissionState === 'requesting'}
                className="vai-flex vai-justify-end vai-gap-2 vai-font-medium"
              >
                <Mic className="vai-w-4 vai-h-4" />
                {permissionState === 'requesting'
                  ? 'Requesting Access...'
                  : 'Allow Microphone Access'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="vai-flex vai-items-start vai-gap-3 vai-w-full vai-mt-4">
        {retryCount >= 3 && (
          <div className="vai-p-3 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded vai-mb-4 vai-w-full">
            <div className="vai-flex vai-items-start vai-gap-3 vai-w-full ">
              <div className="vai-flex-1">
                <h3 className="vai-text-sm vai-font-bold vai-mb-2">
                  Having trouble?
                </h3>
                <p className="vai-text-sm vai-mb-4">
                  You may need to manually enable microphone access in your
                  browser settings.
                </p>
                <div className="vai-flex vai-justify-end">
                  <a
                    href="https://support.google.com/chrome/answer/2693767"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vai-inline-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-text-sm vai-font-medium vai-text-gray-700 vai-bg-white vai-border vai-border-gray-300 vai-rounded-md vai-shadow-sm hover:vai-bg-gray-50 focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-offset-2 focus:vai-ring-blue-500"
                  >
                    <ExternalLink className="vai-w-3 vai-h-3" />
                    Microphone Help Guide
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isAudioUploadEnabled && onAudioFileSelected && showDevTools && (
        <div className="vai-mt-4 vai-pt-4 vai-border-t vai-border-gray-200 vai-w-full vai-flex vai-flex-col vai-items-center">
          <p className="vai-text-sm vai-text-gray-500 vai-mb-2">
            Or upload a pre-recorded audio file
          </p>
          <AudioFileUpload onFileSelected={onAudioFileSelected} />
        </div>
      )}
    </div>
  );
};

export default MicrophonePermissionFlow;
